import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileSpreadsheet, FileText, RefreshCw, BarChart3, Package, Wrench } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';
import { ReportTable, ReportSummary } from '../components/reports/ReportTable';
import { FilterSelect, DateRange, ViewSwitch } from '../components/reports/ReportFilters';
import { exportReportExcel, exportReportPDF } from '../utils/reportExport';

const SALES_VIEWS = [
  { value: 'bill', label: 'Bill Wise' }, { value: 'product', label: 'Product Wise' },
  { value: 'category', label: 'Category Wise' }, { value: 'brand', label: 'Brand Wise' },
  { value: 'serial', label: 'Serial Number Wise' }, { value: 'engineer', label: 'Engineer Wise' },
];
const STOCK_VIEWS = [
  { value: 'current', label: 'Current Available' }, { value: 'brand', label: 'Brand Wise' }, { value: 'category', label: 'Category Wise' },
];
const SERVICE_STATUSES = ['New', 'On Process', 'Closed', 'Canceled'].map(s => ({ value: s, label: s }));

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA'); };
const today = () => new Date().toLocaleDateString('en-CA');
const toOptions = (list) => list.map(x => ({ value: x.id, label: x.name }));

export default function ReportsModule() {
  const [tab, setTab] = useState('sales');
  const [salesView, setSalesView] = useState('bill');
  const [stockView, setStockView] = useState('current');
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [billType, setBillType] = useState('all');
  const [brandId, setBrandId] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [serviceType, setServiceType] = useState('all');
  const [serviceStatus, setServiceStatus] = useState('all');
  const [engineerId, setEngineerId] = useState('all');
  const [masters, setMasters] = useState({ brands: [], categories: [], engineers: [], serviceTypes: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const protectedExport = useProtectedExport();

  useEffect(() => {
    Promise.all([
      api.get('/brands/'), api.get('/categories/'), api.get('/services/engineers/list'),
      api.get('/services/types'), api.get('/business-settings/public'),
    ]).then(([b, c, e, t, bs]) => {
      setMasters({ brands: b.data, categories: c.data, engineers: e.data, serviceTypes: t.data });
      setBusinessName(bs.data.business_name);
    }).catch(() => toast.error('Failed to load filter options'));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      let url, params;
      if (tab === 'sales') {
        url = '/reports/sales'; params = { report_type: salesView, from_date: fromDate, to_date: toDate, bill_type: billType };
      } else if (tab === 'stock') {
        url = '/reports/stock'; params = { report_type: stockView, brand_id: brandId, category_id: categoryId };
      } else {
        url = '/reports/services';
        params = { from_date: fromDate, to_date: toDate, service_type: serviceType, status: serviceStatus, engineer_id: engineerId };
      }
      const res = await api.get(url, { params });
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [tab, salesView, stockView, fromDate, toDate, billType, brandId, categoryId, serviceType, serviceStatus, engineerId]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const exportAs = (kind) => {
    if (!report?.rows.length) return toast.error('Nothing to export');
    protectedExport.requestExport(() => {
      if (kind === 'excel') exportReportExcel(report); else exportReportPDF(report, businessName);
      toast.success(`${kind === 'excel' ? 'Excel' : 'PDF'} downloaded`);
    });
  };

  return (
    <div className="space-y-6" data-testid="reports-module-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Sales, stock and service reports with Excel &amp; PDF export</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportAs('excel')} data-testid="export-excel-btn">
            <FileSpreadsheet size={16} className="mr-2 text-green-600" /> Excel
          </Button>
          <Button variant="outline" onClick={() => exportAs('pdf')} data-testid="export-pdf-btn">
            <FileText size={16} className="mr-2 text-red-600" /> PDF
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setReport(null); setTab(v); }}>
        <TabsList>
          <TabsTrigger value="sales" data-testid="reports-tab-sales"><BarChart3 size={14} className="mr-1.5" />Sales</TabsTrigger>
          <TabsTrigger value="stock" data-testid="reports-tab-stock"><Package size={14} className="mr-1.5" />Stock</TabsTrigger>
          <TabsTrigger value="services" data-testid="reports-tab-services"><Wrench size={14} className="mr-1.5" />Services</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader className="space-y-4">
          {tab === 'sales' && <ViewSwitch prefix="sales" options={SALES_VIEWS} value={salesView} onChange={setSalesView} />}
          {tab === 'stock' && <ViewSwitch prefix="stock" options={STOCK_VIEWS} value={stockView} onChange={setStockView} />}
          <div className="flex flex-wrap items-end gap-3">
            {tab !== 'stock' && <DateRange prefix={tab} from={fromDate} to={toDate} setFrom={setFromDate} setTo={setToDate} />}
            {tab === 'sales' && (
              <FilterSelect label="Bill Type" value={billType} onChange={setBillType} testId="sales-bill-type-filter" allLabel="All Bills"
                options={[{ value: 'GST', label: 'GST' }, { value: 'Cash', label: 'Cash' }]} />
            )}
            {tab === 'stock' && (
              <>
                <FilterSelect label="Brand" value={brandId} onChange={setBrandId} testId="stock-brand-filter" allLabel="All Brands" options={toOptions(masters.brands)} />
                <FilterSelect label="Category" value={categoryId} onChange={setCategoryId} testId="stock-category-filter" allLabel="All Categories" options={toOptions(masters.categories)} />
              </>
            )}
            {tab === 'services' && (
              <>
                <FilterSelect label="Service Type" value={serviceType} onChange={setServiceType} testId="service-type-filter" allLabel="All Types"
                  options={masters.serviceTypes.map(t => ({ value: t, label: t }))} />
                <FilterSelect label="Status" value={serviceStatus} onChange={setServiceStatus} testId="service-status-filter" allLabel="All Status" options={SERVICE_STATUSES} />
                <FilterSelect label="Engineer" value={engineerId} onChange={setEngineerId} testId="service-engineer-filter" allLabel="All Engineers" options={toOptions(masters.engineers)} />
              </>
            )}
            <Button variant="ghost" onClick={loadReport} disabled={loading} data-testid="refresh-report-btn">
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {report && (
            <>
              <div>
                <CardTitle className="text-base" data-testid="report-title">{report.title}</CardTitle>
                <CardDescription>{report.rows.length} row(s)</CardDescription>
              </div>
              <ReportSummary report={report} />
              <ReportTable report={report} />
            </>
          )}
          {!report && loading && <div className="py-16 text-center text-slate-500" data-testid="report-loading">Loading report…</div>}
        </CardContent>
      </Card>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
}
